import React from "react";
import localforage from "localforage";
import { useLocation } from "react-router-dom";
import { checkPermission, usePermissions } from "../PermissionsContext";
import { useUser } from "../UserContext";
import { sessionInfo } from "../../utils/utils";
import {
  getNegotiationModeRevision,
  isNegotiationModeReady,
  onNegotiationModeChange,
} from "../../services/negotiationBridge";
import {
  BidAttempt,
  BidSession,
  BID_ATTEMPT_RESOLVED_EVENT,
  captureBidContext,
  isRecordedBidRefusal,
  PendingBidError,
  StaleBidAttemptError,
  submitBidAttempt,
  usePendingBidAttempts,
} from "../../routes/pages/negotiate.bidAttempt";
import { PendingBidRecoveryView } from "./PendingBidRecoveryView";

/**
 * Recovery remains available when new-bid readiness redirects to another page.
 * @story skip credential lifecycle is covered by CI unit and browser regressions; see PendingBidRecoveryView for visual states
 */
export function PendingBidRecoveryPanel() {
  const location = useLocation();
  const { amaryCourse } = useUser() ?? {};
  const { permissions, permissionsLoaded } = usePermissions();
  const canView = permissionsLoaded && checkPermission(permissions.bid, "view");
  const canCreate = permissionsLoaded && checkPermission(permissions.bid, "create");
  const modeReady = isNegotiationModeReady();
  const modeRevision = getNegotiationModeRevision();
  const [session, setSession] = React.useState<BidSession>();
  const [refresh, setRefresh] = React.useState(0);
  const [sending, setSending] = React.useState<Record<string, boolean>>({});
  const [notice, setNotice] = React.useState<string>();
  const requests = React.useRef(new Map<string, AbortController>());
  const actionAllowed = React.useRef(false);
  actionAllowed.current = canView && canCreate && modeReady;
  const pending = usePendingBidAttempts(
    canView && modeReady ? session : undefined,
    [],
    true,
    refresh,
  );
  const attempts = Object.values(pending.attempts);

  React.useEffect(() => {
    if (canView && canCreate && modeReady) return;
    requests.current.forEach((request) => request.abort());
    requests.current.clear();
    setSending({});
  }, [canView, canCreate, modeReady]);

  React.useEffect(() => {
    const reload = () => setRefresh((value) => value + 1);
    const onVisible = () => {
      if (document.visibilityState === "visible") reload();
    };
    window.addEventListener("focus", reload);
    window.addEventListener("storage", reload);
    document.addEventListener("visibilitychange", onVisible);
    const unsubscribeMode = onNegotiationModeChange(() => {
      requests.current.forEach((request) => request.abort());
      requests.current.clear();
      reload();
    });
    return () => {
      window.removeEventListener("focus", reload);
      window.removeEventListener("storage", reload);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribeMode();
    };
  }, []);

  React.useEffect(() => {
    let disposed = false;
    const activeRequests = requests.current;
    activeRequests.forEach((request) => request.abort());
    activeRequests.clear();
    setSession(undefined);
    setSending({});
    setNotice(undefined);
    const sessionRevision = getNegotiationModeRevision();
    if (isNegotiationModeReady())
      void sessionInfo()
        .then((value) => {
          if (
            !disposed &&
            isNegotiationModeReady() &&
            sessionRevision === getNegotiationModeRevision() &&
            typeof value?.org_id === "string" &&
            typeof value?.iss === "string" &&
            value.org_id &&
            value.iss
          )
            setSession({ org_id: value.org_id, iss: value.iss });
        })
        .catch(() => {});
    return () => {
      disposed = true;
      activeRequests.forEach((request) => request.abort());
      activeRequests.clear();
    };
  }, [location.key, amaryCourse, refresh]);

  async function consult(attempt: BidAttempt) {
    if (
      !session ||
      !canCreate ||
      requests.current.has(attempt.key) ||
      !isNegotiationModeReady() ||
      modeRevision !== getNegotiationModeRevision()
    )
      return;
    const requestRevision = getNegotiationModeRevision();
    // The click binds the namespace and transport before reading the JWT.
    const context = captureBidContext(session, attempt.payload.postId);
    const request = new AbortController();
    requests.current.set(attempt.key, request);
    setSending((previous) => ({ ...previous, [attempt.key]: true }));
    setNotice(undefined);
    try {
      const jwt = await localforage.getItem<string>("JWT");
      if (
        request.signal.aborted ||
        !actionAllowed.current ||
        !isNegotiationModeReady() ||
        requestRevision !== getNegotiationModeRevision()
      )
        return;
      await submitBidAttempt(
        context.session,
        attempt.payload,
        { headers: { "X-JWT": jwt }, timeout: 15_000, signal: request.signal },
        () => {},
        { context, expectedAttempt: attempt },
      );
      if (!request.signal.aborted) window.dispatchEvent(new Event(BID_ATTEMPT_RESOLVED_EVENT));
    } catch (error) {
      if (!request.signal.aborted) {
        const refused = isRecordedBidRefusal(error);
        if (refused) window.dispatchEvent(new Event(BID_ATTEMPT_RESOLVED_EVENT));
        setNotice(
          error instanceof PendingBidError || error instanceof StaleBidAttemptError
            ? error.message
            : refused
              ? "O lance foi recusado e essa tentativa foi encerrada. Atualize as publicações antes de fazer outro lance."
              : "Não foi possível consultar a tentativa salva. Seus registros foram preservados.",
        );
      }
    } finally {
      if (requests.current.get(attempt.key) === request) requests.current.delete(attempt.key);
      if (!request.signal.aborted)
        setSending((previous) => ({ ...previous, [attempt.key]: false }));
    }
  }

  if (!canView || !modeReady || !session || (!attempts.length && !pending.error && !notice))
    return null;
  return (
    <PendingBidRecoveryView
      attempts={attempts}
      storageError={pending.error}
      canConsult={canCreate}
      sending={sending}
      notice={notice}
      onConsult={(attempt) => void consult(attempt)}
      onDismiss={() => setNotice(undefined)}
    />
  );
}
